/**
 * Loudspeaker profiles -- group 07.
 *
 * Eight British, German, French and Chinese-built designs whose engineering
 * choices differ at the level of the radiator and the box, not the finish: a
 * coaxial point source with a tractrix port diffuser, a three-way retro
 * standmount, an inverted-array two-way, a constrained-layer-damped
 * floorstander, an air-motion-transformer three-way, a wave-guided ceramic
 * three-way, a narrow-baffle design with side-firing woofers, and a sealed
 * spherical active with DSP crossovers. `responseTilt` carries the voicing and
 * is authored per design; the alignment-derived baseline underneath it comes
 * from the quoted bass extension. Most of these manufacturers publish
 * sensitivity, impedance and extension but nothing about anechoic response or
 * distortion, so those parts are modelled and tagged as such.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_07: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Fyne Audio',
    model: 'F1-8',
    price: usd(7000, 'APPROXIMATE', 2021, 'Price per pair. US retail has been quoted in the 6,500-7,500 USD range; treated as approximate.'),
    year: 2019,
    country: 'United Kingdom',
    speakerType: 'coaxial',
    enclosure: 'ported',
    drivers: ['coaxial-point-source', 'dynamic'],
    driverCount: 1,
    wayCount: 2,
    sensitivityDb: 92,
    nominalOhms: 8,
    minOhms: 6.2,
    bass: [38, 31],
    hfExtension: 26000,
    portTuningHz: 34,
    crossovers: [[1700, 12]],
    responseTilt: [[20, 0], [45, 0.6], [110, -0.3], [350, -0.5], [900, 0.3], [1800, -0.9], [3500, 0.8], [8000, 0.4], [16000, -1.2]],
    resonances: [[190, -32, 10], [520, -36, 13]],
    coverageAngleDeg: 90,
    recommendedWatts: [25, 250],
    dimensionsMm: [520, 330, 350],
    massKg: 24,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#b98f52',
    tags: ['point-source', 'tractrix-port', 'compression-loaded-tweeter'],
    notes:
      'The tweeter fires through the throat of the bass cone, so acoustic centres coincide and the vertical polar response does not develop the lobing error a spaced two-way shows through crossover. The trade is a discontinuity at the cone-to-horn junction, modelled here as a narrow suck-out around the 1.7 kHz crossover followed by a lift where the cone acts as a shallow horn flare. Bass loading is a downward-firing port terminated in a tractrix diffuser, which raises the frequency at which port air velocity turns into audible chuffing, so port compression is modelled as low for a 38 Hz alignment.',
    provenanceNote:
      'The coaxial point-source driver, the downward-firing tractrix-terminated port and the crossover region come from Fyne Audio published material. Sensitivity, impedance minimum, bass extension, mass and dimensions are approximate rather than confirmed, and the anechoic response, cabinet resonances and distortion are modelled from the alignment. Tagged ESTIMATED throughout.',
  }),

  makeSpeaker({
    mfr: 'Wharfedale',
    model: 'Linton',
    price: usd(1499, 'APPROXIMATE', 2023, 'Price per pair including the matching stands, which are part of the system; treated as approximate.'),
    year: 2019,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 90,
    nominalOhms: 6,
    minOhms: 4.4,
    bass: [40, 32],
    hfExtension: 20000,
    portTuningHz: 38,
    crossovers: [[630, 12], [2400, 12]],
    responseTilt: [[20, 0], [55, 1.0], [120, 0.6], [300, -0.2], [900, -0.5], [2500, -1.2], [5000, -0.4], [10000, -0.8], [20000, -2.2]],
    resonances: [[160, -27, 7], [380, -31, 9]],
    coverageAngleDeg: 130,
    recommendedWatts: [25, 200],
    dimensionsMm: [565, 300, 330],
    massKg: 18.5,
    thdPercent: 0.45,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a08050',
    tags: ['three-way', 'retro-alignment', 'midrange-dome'],
    notes:
      'A large three-way in a lightly braced wooden box, which is a specific set of engineering choices rather than a styling exercise: a dedicated midrange driver keeps the 630 Hz to 2.4 kHz band away from both the woofer breakup region and the tweeter excursion limit, and the wide 300 mm baffle puts the baffle step near 380 Hz. The cabinet is allowed to contribute at low level and low Q, modelled as two resonances in the lower midrange rather than the high-Q signature of a heavily braced box, and the voicing carries a deliberate upper-midrange recession with a 1 dB midbass lift on top of a 40 Hz alignment.',
    provenanceNote:
      'Sensitivity, nominal impedance, bass extension, driver complement, dimensions and mass follow Wharfedale published specifications for the Linton, which is why this entry carries MANUFACTURER_SPEC. The anechoic response shape, voicing offsets, cabinet resonance levels, port tuning and distortion figure are modelled and are not Wharfedale claims.',
  }),

  makeSpeaker({
    mfr: 'Mission',
    model: '770',
    price: usd(3999, 'APPROXIMATE', 2023, 'Price per pair including the dedicated stands; treated as approximate.'),
    year: 2022,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 88,
    nominalOhms: 8,
    minOhms: 5.8,
    bass: [42, 34],
    hfExtension: 22000,
    portTuningHz: 40,
    crossovers: [{ freqHz: 2500, slopeDbOct: 12, alignment: 'butterworth', offsetMm: 35 }],
    responseTilt: [[20, 0], [60, -0.4], [150, 0.2], [500, 0.4], [1500, 0.9], [3000, 0.6], [6000, -0.6], [12000, 0.4], [20000, -1.0]],
    resonances: [[210, -29, 8], [560, -34, 11]],
    coverageAngleDeg: 120,
    recommendedWatts: [25, 150],
    dimensionsMm: [480, 285, 300],
    massKg: 13.5,
    thdPercent: 0.5,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#6b7f8c',
    tags: ['inverted-array', 'time-alignment', 'polypropylene-cone'],
    notes:
      'The tweeter sits below the woofer rather than above it, which is the defining choice: with the listener seated, the tweeter is the further of the two drivers from the ear, so its arrival is delayed toward the woofer instead of leading it. That is modelled here as a 35 mm inter-driver offset through a second-order Butterworth crossover, which shifts the vertical lobe upward and changes the phase relationship through the crossover region compared with a conventional array. The voicing is forward through the presence band, roughly 1 dB up at 1.5 kHz, with a light 42 Hz alignment that suits stand mounting away from walls.',
    provenanceNote:
      'The inverted driver array and the two-way configuration come from Mission published material for the 770 reissue. Sensitivity, impedance, bass extension, port tuning, crossover frequency, the 35 mm driver offset, mass and dimensions are estimates rather than confirmed specifications, and the response and distortion are modelled. Tagged ESTIMATED.',
  }),

  makeSpeaker({
    mfr: 'Q Acoustics',
    model: 'Concept 500',
    price: usd(5999, 'ORIGINAL_RETAIL', 2017, 'Price per pair at US launch.'),
    year: 2017,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 90,
    nominalOhms: 6,
    minOhms: 4.0,
    bass: [41, 33],
    hfExtension: 24000,
    portTuningHz: 38,
    crossovers: [[2500, 18]],
    responseTilt: [[20, 0], [50, 0.4], [140, -0.4], [400, -0.3], [1200, 0.2], [3000, -0.7], [7000, 0.5], [13000, 0.3], [20000, -0.9]],
    resonances: [[240, -44, 20], [610, -47, 24]],
    coverageAngleDeg: 115,
    recommendedWatts: [25, 200],
    dimensionsMm: [1006, 260, 320],
    massKg: 31,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f8fa8',
    tags: ['constrained-layer-damping', 'point-to-point-bracing', 'decoupled-tweeter'],
    notes:
      'The engineering content is in the box. A non-setting adhesive layer between two cabinet skins converts panel flexure into shear and therefore into heat, and internal point-to-point bracing raises what remains in frequency and lowers its Q, which is why this profile carries cabinet resonances around 20 dB lower than the wooden-box entries in this group. The tweeter is mechanically decoupled from that structure so woofer-driven cabinet motion does not modulate it. A 2.5 kHz crossover between a 28 mm dome and two 165 mm mid-bass units in a 2.5-way arrangement puts the acoustic handover just above the baffle-step region for a 260 mm baffle.',
    provenanceNote:
      'Sensitivity, nominal and minimum impedance, bass extension, crossover frequency, dimensions, mass and the Gelcore constrained-layer and point-to-point bracing construction follow Q Acoustics published material for the Concept 500, hence MANUFACTURER_SPEC. The anechoic response, voicing offsets, the resonance levels attributed to the damped cabinet and the distortion figure are modelled.',
  }),

  makeSpeaker({
    mfr: 'ELAC',
    model: 'Vela FS 408',
    price: usd(4000, 'APPROXIMATE', 2021, 'Price per pair. US retail has been quoted near 4,000 USD; treated as approximate.'),
    year: 2018,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['amt', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.4,
    bass: [34, 28],
    hfExtension: 50000,
    portTuningHz: 32,
    crossovers: [[420, 24], [2700, 24]],
    responseTilt: [[20, 0], [45, 0.5], [130, -0.5], [500, -0.2], [2000, 0.3], [4000, -0.4], [9000, 0.9], [15000, 1.1], [25000, 0.3]],
    resonances: [[155, -38, 15], [430, -42, 18]],
    coverageAngleDeg: 100,
    recommendedWatts: [40, 280],
    dimensionsMm: [1080, 220, 300],
    massKg: 25,
    thdPercent: 0.28,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#5f8fb0',
    tags: ['air-motion-transformer', 'folded-diaphragm', 'three-way'],
    notes:
      'The high-frequency unit is a folded pleated diaphragm that squeezes air out of its folds, so it moves several times more air than a dome of the same area for the same excursion. Two modelled consequences follow: usable output past 40 kHz, carried here as a 50 kHz limit and a rising top octave in the voicing, and low distortion at the top because the diaphragm is barely displacing. The pleated structure is also wider than it is tall, so horizontal dispersion stays broad while vertical narrows, modelled as a 100 degree coverage angle. A 2.7 kHz fourth-order handover keeps the folded unit out of its lower range where the pleats no longer behave as a single radiator.',
    provenanceNote:
      'The JET air-motion-transformer tweeter, three-way configuration and 4 ohm nominal impedance follow ELAC published material. Sensitivity, minimum impedance, bass extension, crossover frequencies, port tuning, mass and dimensions are approximate, and the response, directivity and distortion are modelled. Tagged ESTIMATED.',
  }),

  makeSpeaker({
    mfr: 'Canton',
    model: 'Reference 5K',
    price: usd(9000, 'APPROXIMATE', 2021, 'Price per pair. European list converted and rounded; treated as approximate.'),
    year: 2019,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [26, 21],
    hfExtension: 30000,
    portTuningHz: 25,
    crossovers: [[220, 12], [3000, 12]],
    responseTilt: [[20, 0], [40, 0.8], [100, 0.3], [300, -0.4], [1000, -0.2], [2500, -0.8], [5000, 0.7], [10000, 0.6], [20000, -0.5]],
    resonances: [[135, -40, 17], [365, -43, 21]],
    coverageAngleDeg: 105,
    recommendedWatts: [60, 350],
    dimensionsMm: [1180, 260, 400],
    massKg: 45,
    thdPercent: 0.32,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#7a7f6b',
    tags: ['ceramic-tweeter', 'waveguide', 'deep-alignment', 'excursion-limited'],
    notes:
      'A hard aluminium-oxide dome set in a shallow waveguide, crossed low at 220 Hz to a dedicated midrange and again at 3 kHz. The waveguide is doing acoustic work: it raises on-axis output either side of 5 kHz and narrows the pattern where the dome would otherwise be beaming, which is why the voicing shows a lift through the top two octaves rather than the roll-off a bare dome on a flat baffle would give. The alignment goes deeper than anything else in this group -- 26 Hz with a 25 Hz port tuning -- so excursion-limited distortion is modelled as rising from about 35 Hz downward, and room gain matters more than for the sealed or standmount entries.',
    provenanceNote:
      'The ceramic-oxide dome, waveguide loading, three-way configuration and 4 ohm nominal impedance follow Canton published material for the Reference series. Sensitivity, minimum impedance, bass extension, port tuning, crossover frequencies, mass and dimensions are estimates rather than confirmed figures, and the response, directivity and distortion are modelled. Tagged ESTIMATED.',
  }),

  makeSpeaker({
    mfr: 'Audio Physic',
    model: 'Codex',
    price: usd(13000, 'APPROXIMATE', 2021, 'Price per pair. European list converted and rounded; treated as approximate.'),
    year: 2016,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 5,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 4,
    minOhms: 3.0,
    bass: [28, 23],
    hfExtension: 28000,
    portTuningHz: 27,
    crossovers: [[150, 12], [2400, 12]],
    responseTilt: [[20, 0], [40, -0.3], [90, 0.5], [250, -0.6], [700, 0.2], [2000, -0.3], [5000, 0.4], [10000, -0.4], [20000, -1.4]],
    resonances: [[125, -41, 18], [340, -45, 22]],
    coverageAngleDeg: 135,
    baffleStepHz: 520,
    recommendedWatts: [40, 300],
    dimensionsMm: [1150, 235, 425],
    massKg: 42,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#5c6b7a',
    tags: ['narrow-baffle', 'side-firing-woofers', 'low-crossover', 'decoupled-drivers'],
    notes:
      'Two geometry decisions dominate this profile. The front baffle is only 235 mm wide with the woofers moved to the side panels, so the front face presents almost no edge for diffraction and the baffle step lands high, near 520 Hz, rather than in the low midrange where it is harder to compensate. The woofers fire in opposing pairs, so their reaction forces cancel in the cabinet instead of shaking it. The midrange handover at 150 Hz is unusually low, keeping the entire vocal range on a single driver: no crossover phase rotation sits inside the band the ear is most sensitive to, which is the specific thing this entry contributes to the group.',
    provenanceNote:
      'The narrow front baffle, side-mounted opposed woofers, decoupled driver mounting and the low midrange crossover come from Audio Physic published descriptions. Sensitivity, impedance minimum, bass extension, crossover frequencies, port tuning, mass and dimensions are estimates, and the response, directivity and distortion are modelled. Tagged ESTIMATED.',
  }),

  makeSpeaker({
    mfr: 'Cabasse',
    model: 'The Pearl',
    price: usd(17000, 'APPROXIMATE', 2021, 'Price per pair; each sphere is an independent active loudspeaker. European list converted and rounded, treated as approximate.'),
    year: 2019,
    country: 'France',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['coaxial-point-source', 'dynamic'],
    driverCount: 3,
    wayCount: 4,
    active: true,
    sensitivityDb: 90,
    nominalOhms: 8,
    minOhms: 7.0,
    bass: [30, 25],
    hfExtension: 27000,
    crossovers: [
      { freqHz: 190, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 880, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 3500, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [35, 0.2], [100, 0.0], [300, -0.2], [1000, 0.1], [3000, -0.5], [8000, 0.2], [14000, -0.3], [20000, -0.8]],
    resonances: [[420, -50, 26]],
    coverageAngleDeg: 150,
    dimensionsMm: [400, 400, 400],
    massKg: 28,
    thdPercent: 0.2,
    harmonicShape: HARMONIC_SHAPES.speaker,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#4f7f8f',
    tags: ['active', 'dsp-crossover', 'sealed-sphere', 'opposed-woofers', 'point-source'],
    notes:
      'A sealed sphere has no parallel walls and no edges, so there is neither an internal standing-wave mode in the audio band nor a diffraction signature, and the single modelled cabinet resonance is a high-frequency, high-Q shell mode 50 dB down. All three crossovers are 48 dB per octave in the digital domain ahead of the amplifiers, which is an order of magnitude steeper than any passive network here and keeps each radiator inside its piston band; the concentric arrangement keeps the acoustic centres coincident so the steep slopes do not create a lobing penalty. Opposed woofers cancel reaction force in a 400 mm sphere, and the sealed alignment gives a second-order roll-off from 30 Hz that integrates with room gain instead of falling off a fourth-order cliff.',
    provenanceNote:
      'The sealed spherical enclosure, the concentric multi-way driver, opposed woofers and DSP-controlled active crossovers come from Cabasse published material. The sensitivity and impedance values are placeholders required by the schema and carry no meaning for an active loudspeaker whose internal amplifiers set the level and see a fixed load; they should not be read as Cabasse figures. Bass extension, crossover frequencies, mass and dimensions are approximate, and the response, directivity and distortion are modelled. Tagged ESTIMATED.',
  }),
];
